<template>
  <div class="event-inner flex">
    <div class="main-content">
      <HeaderBreadcrumb :breadcrumbList="breadcrumbs" />

      <div class="eventpay-container">
        <div class="eventpay-card">
          <!-- Header with event details -->
          <div class="eventpay-header">
            <div class="header-icon-wrapper">
              <font-awesome-icon
                :icon="['fas', 'credit-card']"
                class="header-icon"
              />
            </div>
            <div class="header-content">
              <h1 class="header-title">
                {{
                  translate("Pay for %s", [eventType.vocative.toLowerCase()])
                }}
              </h1>
              <p class="header-subtitle">{{ event.title }}</p>
            </div>
          </div>

          <!-- Loading state for product selection -->
          <div v-if="disableProductSelection === null" class="loader-container">
            <ApiLoader />
          </div>

          <!-- Main content area -->
          <div class="eventpay-content">
            <!-- Step Indicator -->
            <StepIndicator
              :currentStep="currentStep"
              :steps="stepTitles"
              :disableProductSelection="disableProductSelection"
              @step-click="handleStepClick"
            />

            <!-- Step 1: Product Selection -->
            <div v-if="currentStep === 1" class="step-container">
              <ProductSelection
                :products="products"
                @next-step="updateSelectedItems"
              />
            </div>

            <!-- Step 2: Recapitulation -->
            <div v-else-if="currentStep === 2" class="step-container">
              <RecapitulationPage
                :attendee="attendee"
                @confirm="handleRecapitulationConfirm"
                @remove-item="removeItem"
                @refresh-attendee="fetchEventDetails"
              />
            </div>

            <!-- Step 3: Order Confirmation -->
            <div v-else-if="currentStep === 3" class="step-container">
              <OrderConfirmation @confirm="handleOrderConfirm" />
            </div>

            <!-- Step 4: Payment -->
            <div v-else-if="currentStep === 4" class="step-container">
              <div class="hidden">
                {{
                  console.log(
                    "EventPay attendee before passing to SelectPayment:",
                    attendee
                  )
                }}
              </div>
              <SelectPayment
                :attendee="attendee"
                :user-email="attendee?.user?.email"
                @pay="handlePayment"
              />
            </div>

            <!-- Step 5: Success Message -->
            <div v-else-if="currentStep === 5" class="step-container">
              <PaymentSuccessMessage
                :eventId="route.params.id"
                :eventType="route.params.type"
              />
            </div>
          </div>

          <!-- Footer with navigation buttons -->
          <div v-if="currentStep !== 5" class="eventpay-footer">
            <!-- Back Button (first step) -->
            <router-link
              v-if="currentStep === 1"
              :to="{
                name: 'EventPage',
                params: { type: route.params.type, id: route.params.id },
              }"
              class="back-button"
            >
              <font-awesome-icon icon="chevron-left" class="button-icon" />
              <span>{{
                $translate("Back to %s", [eventType.name.toLowerCase()])
              }}</span>
            </router-link>

            <!-- Previous Button (other steps) -->
            <button
              v-else-if="footerButtonPrevText"
              type="button"
              @click="handlePrevStepClick"
              class="prev-button"
            >
              <font-awesome-icon icon="chevron-left" class="button-icon" />
              <span>{{ footerButtonPrevText }}</span>
            </button>

            <!-- Next Button (except confirmation page) -->
            <button
              v-if="footerButtonNextText && currentStep !== 3"
              type="button"
              :disabled="isButtonDisabled"
              @click="handleFooterButtonClick"
              class="next-button"
              :class="{ 'next-button-disabled': isButtonDisabled }"
            >
              <span>{{ footerButtonNextText }}</span>
              <font-awesome-icon icon="chevron-right" class="button-icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <RightSidebar />

    <ProcessingPaymentModal v-if="isProcessingPayment" />
  </div>
</template>

<script>
import {
  computed,
  ref,
  watch,
  onMounted,
  onUnmounted,
  inject,
  provide,
  onBeforeUnmount,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { eventDetails } from "@/composables/eventDetails";
import { useProducts } from "@/composables/useProducts";
import { initializeSocket } from "@/utils/socket";
import { translate } from "@/utils/translate";
import { notify } from "notiwind";
import { checkUserSession } from "@/composables/checkSession";
import { getEventType, getSystemPath } from "@/utils/helper";
import { trackEvent } from "@/utils/trackingManager";
import OrderConfirmation from "@/components/payment/pages/OrderConfirmation.vue";
import PaymentSuccessMessage from "@/components/payment/pages/PaymentSuccessMessage.vue";
import HeaderBreadcrumb from "@/components/general/HeaderBreadcrumb.vue";
import StepIndicator from "@/components/payment/partials/StepIndicator.vue";
import ProductSelection from "@/components/payment/pages/ProductSelection.vue";
import RecapitulationPage from "@/components/payment/pages/RecapitulationPage.vue";
import SelectPayment from "@/components/payment/pages/SelectPayment.vue";
import RightSidebar from "@/components/payment/partials/RightSidebar.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import ProcessingPaymentModal from "@/components/modals/ProcessingPaymentModal.vue";
import ApiLoader from "@/components/general/ApiLoader.vue";

export default {
  name: "EventPay",
  components: {
    FontAwesomeIcon,
    HeaderBreadcrumb,
    StepIndicator,
    ProductSelection,
    RecapitulationPage,
    OrderConfirmation,
    PaymentSuccessMessage,
    SelectPayment,
    RightSidebar,
    ProcessingPaymentModal,
    ApiLoader,
  },
  setup() {
    const cartStore = inject("cartStore");
    const route = useRoute();
    const router = useRouter();
    const eventId = ref(route.params.id);
    const eventType = getEventType(route.params.type);
    const isProcessingPayment = ref(false);
    const processingTimeout = ref(null);
    const { userSession, fetchUserStatus } = checkUserSession();
    const isInitialSetup = ref(true);

    // Current step in the payment process
    const currentStep = ref(1);

    // Provide current step to child components
    provide("currentStep", currentStep);

    // Initialize session
    fetchUserStatus(route.params.id);

    const {
      event,
      attendee,
      isSignedUp,
      isPaid,
      eventState,
      fetchEventDetails,
      signup,
      pay,
    } = eventDetails(eventId.value, true);

    const eventPage = () => {
      try {
        router.push({
          name: "EventPage",
          params: {
            type: route.params.type,
            id: route.params.id,
          },
        });
        console.log(
          "Navigating to event page with name: EventPage, params:",
          route.params
        );
      } catch (error) {
        console.error("Error navigating to event page:", error);
      }
    };

    watch(
      userSession,
      (data) => {
        if (data.status) {
          if (
            data.event &&
            data.event.joined &&
            data.event.paid &&
            data.event.payment_required
          ) {
            console.log(
              "User session status is valid, redirecting to event page."
            );
            eventPage();
            notify(
              {
                title: translate("Success"),
                text: translate("Payment has been received successfully"),
                type: "success",
                group: "info",
              },
              5000
            );
          }
        } else {
          if (data.message) {
            notify(
              {
                title: translate("Info"),
                text: data.message,
                type: "info",
                group: "info",
              },
              5000
            );
          }
          eventPage();
        }
      },
      { deep: true }
    );

    const breadcrumbs = computed(() => {
      const payPath = getSystemPath("pay");
      return [
        { text: translate("Events"), to: "/" },
        {
          text: eventType.plural,
          to: { name: "EventListType", params: { type: eventType.path } },
        },
        {
          text: event.value.title,
          to: {
            name: "EventPage",
            params: { type: route.params.type, id: route.params.id },
          },
        },
        {
          text: translate("Payment"),
          to: {
            name: "EventPay",
            params: {
              type: route.params.type,
              id: route.params.id,
              path: payPath,
            },
          },
        },
      ];
    });

    const disableProductSelection = ref(null);
    const { products, fetchProducts } = useProducts(
      window.vueAppConfig.info.id,
      eventId.value
    );
    const stepTitles = ref([
      translate("Select Package"),
      translate("Review & Confirm"),
      translate("Order Confirmation"),
      translate("Payment"),
    ]);

    const isButtonDisabled = computed(() => {
      return (
        cartStore.state.isLoading ||
        (currentStep.value === 1 &&
          (cartStore.state.cart.length === 0 ||
            cartStore.state.total.total <= 0))
      );
    });

    const footerButtonNextText = computed(() => {
      if (currentStep.value === 1) {
        return translate("Continue to Summary");
      } else if (currentStep.value === 2) {
        return translate("Continue to Order Confirmation");
      } else if (currentStep.value === 3) {
        return translate("Create Order with Payment Obligation");
      } else {
        return false; // hide button
      }
    });

    const footerButtonPrevText = computed(() => {
      if (currentStep.value === 1) {
        return false;
      } else if (currentStep.value === 2 && !disableProductSelection.value) {
        return translate("Back to Options");
      } else if (currentStep.value === 2 && disableProductSelection.value) {
        return translate("Back to %s", [eventType.name.toLowerCase()]); // Optional, you could also return `false` if you don't want any button here.
      } else if (currentStep.value === 3) {
        return translate("Back to Summary");
      } else if (currentStep.value === 4) {
        return translate("Back to Event", []);
      } else {
        return false;
      }
    });

    const handleRecapitulationConfirm = () => {
      currentStep.value = 3;
    };

    const handleOrderConfirm = () => {
      currentStep.value = 4;
    };

    const handlePayment = async (paymentMethod) => {
      try {
        // const response = await pay(selectedItems.value, paymentMethod);
        const response = await pay(cartStore.state.cart, paymentMethod);
        if (response.status) {
          // Purchase tracking is now handled by the backend

          // Show success message instead of redirecting immediately
          currentStep.value = 5;

          notify(
            {
              title: translate("Success"),
              text: translate("Payment has been received successfully"),
              type: "success",
              group: "info",
            },
            5000
          );
          // Don't redirect immediately
          // eventPage();
        }
      } catch (error) {
        console.error("Error processing payment:", error);
      }
    };

    const refreshCartContent = async () => {
      await cartStore.fetchCartContent();
    };

    const updateSelectedItems = () => {
      if (cartStore.state.cart.length > 0) {
        currentStep.value = 2;
      } else {
        notify(
          {
            title: translate("Your cart is empty!"),
            text: translate("Please select at least one item to continue."),
            type: "info",
            group: "info",
          },
          5000
        );
      }
    };
    const checkForSingleProduct = async () => {
      if (
        products.value.events.length === 1 &&
        products.value.packages.length === 0
      ) {
        const productId = products.value.events[0].id;
        const alreadyInCart = cartStore.state.cart.some(
          (item) => item.product_id === productId && item.type === "event"
        );
        if (!alreadyInCart) {
          await cartStore.addItemToCart(
            "event",
            products.value.events[0],
            false
          );
          
          // Track add_to_cart event when auto-adding products
          const product = products.value.events[0];
          trackEvent("ecommerce", "add_to_cart", "event", product.price, {
            item_id: product.id,
            item_name: product.title || product.name,
            price: product.price,
            quantity: 1,
          });
        }
        currentStep.value = 2; // Directly go to the next step (Recapitulation)
        disableProductSelection.value = true;
        return true;
      }
      disableProductSelection.value = false; // No product selection
      return false;
    };
    const handleFooterButtonClick = () => {
      if (
        currentStep.value === 1 &&
        (cartStore.state.cart.length === 0 || cartStore.state.total.total <= 0)
      ) {
        notify(
          {
            title: translate("Your cart is empty!"),
            text: translate("Please select at least one item to continue."),
            type: "info",
            group: "info",
          },
          5000
        );
      } else if (currentStep.value === 1) {
        updateSelectedItems();
      } else if (currentStep.value === 2) {
        handleRecapitulationConfirm();
      } else if (currentStep.value === 3) {
        handleOrderConfirm();
      }
    };

    const handlePaymentClick = () => {
      handlePayment();
    };

    const handleCartEmpty = async () => {
      if (!(await checkForSingleProduct())) {
        notify(
          {
            title: translate("Your cart is empty!"),
            text: translate("Please select at least one item to continue."),
            type: "info",
            group: "info",
          },
          5000
        );
        currentStep.value = 1;
      }
    };

    const removeItem = async (item) => {
      await cartStore.removeItem(item.id, item.type);
      if (cartStore.state.cart.length === 0) {
        handleCartEmpty();
      }
    };

    const handleStepClick = (step) => {
      if (step < currentStep.value) {
        currentStep.value = step;
      } else if (step === 1) {
        if (
          cartStore.state.cart.length === 0 ||
          cartStore.state.total.total <= 0
        ) {
          handleCartEmpty();
        }
      } else if (step === 2) {
        updateSelectedItems();
      } else if (step === 3 && currentStep.value === 2) {
        handleRecapitulationConfirm();
      } else if (step === 4 && currentStep.value === 3) {
        handleOrderConfirm();
      }
    };

    const handlePrevStepClick = () => {
      if (currentStep.value > 1) {
        // From step 4 (payment), go back to event page
        if (currentStep.value === 4) {
          router.push({
            name: "EventPage",
            params: {
              type: route.params.type,
              id: route.params.id,
            },
          });
          return;
        }
        // From step 2 with disabled product selection, go back to event page
        if (currentStep.value === 2 && disableProductSelection.value) {
          router.push({
            name: "EventPage",
            params: {
              type: route.params.type,
              id: route.params.id,
            },
          });
          return;
        }
        // Normal step back
        currentStep.value -= 1;
      } else {
        router.push({
          name: "EventPage",
          params: {
            type: route.params.type,
            id: route.params.id,
          },
        });
      }
    };
    /*watch(() => cartStore.state.total.total, (newTotal) => {
      console.log('cartStore.state.total.total updated:', newTotal);
      if (newTotal <= 0) {
        alert('ok');
        handleCartEmpty();
      }
    });
    // Watcher for cart state to check if it's empty
    watch(() => cartStore.state.cart, (newCart) => {
      console.log('cartStore.state.cart updated:', newCart);
      if (newCart.length === 0 || cartStore.state.total.total <= 0) {
        handleCartEmpty();
      }
    })*/

    watch(
      () => ({
        total: cartStore.state.total.total,
        cart: cartStore.state.cart,
      }),
      (newState) => {
        if (
          !isInitialSetup.value &&
          (newState.total <= 0 || newState.cart.length === 0)
        ) {
          handleCartEmpty();
        }
      }
    );

    // Watcher for selected items to debug their state
    /*  watch(selectedItems, (newSelectedItems) => {
        console.log('selectedItems updated:', newSelectedItems);
      });*/

    onMounted(async () => {
      try {
        await fetchEventDetails();
        await fetchProducts();
        await refreshCartContent();
        await checkForSingleProduct();
        isInitialSetup.value = false; // Initial setup is done
        if (event.value && event.value.id) {
          initializeSocket(event.value.id, (message) => {
            //console.log("Socket message received:", message);
            if (
              message.type === "update" &&
              message.data.id === event.value.id
            ) {
              fetchEventDetails();
            }
            if (message.type === "payment_confirmation") {
              // show notification and redirect to event page
              notify(
                {
                  title: translate("Success"),
                  text: translate("Payment has been received successfully"),
                  type: "info",
                  group: "info",
                },
                15000
              );
              eventPage();
            }
            if (message.type === "cart_update") {
              refreshCartContent();
            }
            if (message.type === "cart_address") {
              currentStep.value = 2;
              // Reload attendee data and pass it to RecapitulationPage
              fetchEventDetails(); // Ensure this updates attendee details
            }
            if (message.type === "cart_control") {
              if (message.data.action === "options") {
                currentStep.value = 1;
              } else if (message.data.action === "summary") {
                currentStep.value = 2;
              } else if (message.data.action === "confirmation") {
                currentStep.value = 3;
              } else if (message.data.action === "payment") {
                currentStep.value = 4;
              } else if (message.data.action === "success") {
                currentStep.value = 5;
              }
              console.log("Remote control message received:", message);
            }
          });
        }

        if (route.query.id) {
          // Show the "Payment Processing" modal and blur effect
          isProcessingPayment.value = true;

          // Set a timeout to hide the modal after 10 seconds
          processingTimeout.value = setTimeout(() => {
            isProcessingPayment.value = false;
            // Remove 'id' parameter from the URL without reloading the page
            router.replace({ query: { ...route.query, id: undefined } });
          }, 30000);
        }
      } catch (error) {
        console.error("Error during onMounted:", error);
      }
    });
    onBeforeUnmount(() => {
      if (processingTimeout.value) {
        clearTimeout(processingTimeout.value);
      }
    });
    onUnmounted(() => {
      // Cleanup WebSocket connection if necessary
    });

    return {
      fetchEventDetails,
      event,
      attendee,
      isSignedUp,
      isPaid,
      eventState,
      signup,
      pay,
      eventType,
      breadcrumbs,
      route,
      currentStep,
      products,
      //  selectedItems,
      stepTitles,
      isButtonDisabled,
      footerButtonNextText,
      footerButtonPrevText,
      handleRecapitulationConfirm,
      handleOrderConfirm,
      handlePayment,
      refreshCartContent,
      handleFooterButtonClick,
      handlePaymentClick,
      updateSelectedItems,
      handleCartEmpty,
      removeItem,
      handleStepClick,
      handlePrevStepClick,
      isProcessingPayment,
      translate,
      disableProductSelection,
    };
  },
};
</script>

<style scoped>
/* Main container */
.event-inner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: auto;
  z-index: 9999999999;
  background-color: #6366f10f;
  display: flex;
}

.main-content {
  width: 75%;
}

/* Event pay container and card */
.eventpay-container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem;
}

.eventpay-card {
  width: 100%;
  max-width: 1024px;
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: card-appear 0.5s ease-out;
}

@keyframes card-appear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header section */
.eventpay-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  background-image: linear-gradient(to right, #f9fafb, #f3f4f6);
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  overflow: hidden;
}

.header-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  background-color: #6366f1;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 8px rgba(99, 102, 241, 0.15);
  position: relative;
  z-index: 1;
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(99, 102, 241, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
}

.header-icon {
  color: white;
  width: 1.75rem;
  height: 1.75rem;
  animation: fadeIn 0.5s ease-out 0.3s both;
}

.header-content {
  flex: 1;
  position: relative;
  z-index: 1;
}

.header-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem;
  animation: slideRight 0.4s ease-out both;
}

.header-subtitle {
  font-size: 1.125rem;
  color: #6b7280;
  margin: 0;
  animation: slideRight 0.4s ease-out 0.1s both;
}

@keyframes slideRight {
  from {
    opacity: 0;
    transform: translateX(-15px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Content area */
.eventpay-content {
  padding: 2rem;
  position: relative;
}

.eventpay-content::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background-image: linear-gradient(to right, #6366f1, #8b5cf6, #d946ef);
  opacity: 0.2;
}

.step-container {
  margin-top: 2rem;
  transition: all 0.3s ease;
  animation: fadeUp 0.4s ease-out 0.1s both;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Loading state */
.loader-container {
  display: flex;
  justify-content: center;
  padding: 3rem;
  min-height: 200px;
}

.overlay-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(3px);
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.3s ease;
}

/* Footer with buttons */
.eventpay-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  position: relative;
}

.eventpay-footer::after {
  content: "";
  position: absolute;
  top: 1px;
  left: 0;
  right: 0;
  height: 1px;
  background-image: linear-gradient(
    to right,
    rgba(99, 102, 241, 0.3),
    rgba(139, 92, 246, 0.3)
  );
}

.back-button,
.prev-button {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background-color: transparent;
  border: 1px solid #d1d5db;
  color: #4b5563;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.back-button::before,
.prev-button::before {
  content: "";
  position: absolute;
  left: 0;
  width: 0;
  height: 100%;
  background-color: rgba(99, 102, 241, 0.1);
  transition: width 0.2s ease;
  z-index: 0;
}

.back-button:hover::before,
.prev-button:hover::before {
  width: 100%;
}

.back-button:hover,
.prev-button:hover {
  border-color: #6366f1;
  color: #4f46e5;
}

.back-button span,
.prev-button span,
.back-button .button-icon,
.prev-button .button-icon {
  position: relative;
  z-index: 1;
}

.next-button {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #6366f1;
  border: none;
  color: white;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px rgba(99, 102, 241, 0.15);
  position: relative;
  overflow: hidden;
}

.next-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, #6366f1, #8b5cf6);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.next-button:hover:not(:disabled)::before {
  opacity: 1;
}

.next-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(99, 102, 241, 0.25);
}

.next-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.15);
}

.next-button-disabled {
  background-color: #9ca3af !important;
  cursor: not-allowed;
  opacity: 0.7;
  box-shadow: none;
}

.next-button span,
.next-button .button-icon {
  position: relative;
  z-index: 1;
}

.button-icon {
  font-size: 1rem;
  transition: transform 0.2s ease;
}

.prev-button:hover .button-icon,
.back-button:hover .button-icon {
  transform: translateX(-3px);
}

.next-button:hover:not(:disabled) .button-icon {
  transform: translateX(3px);
}

/* Responsive adjustments */
@media (max-width: 1447px) {
  .event-inner {
    justify-content: center;
  }

  .main-content {
    width: 100%;
  }
}

@media (max-width: 900px) {
  .eventpay-container {
    padding: 1.5rem 1rem;
  }

  .eventpay-card {
    border-radius: 0.75rem;
  }
}

@media (max-width: 768px) {
  .eventpay-header {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
    gap: 1rem;
  }

  .eventpay-content {
    padding: 1.5rem;
  }

  .eventpay-footer {
    padding: 1.25rem;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .back-button,
  .prev-button,
  .next-button {
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
  }
}

@media (max-width: 480px) {
  .eventpay-container {
    padding: 0.75rem;
  }

  .eventpay-header {
    padding: 1.25rem 1rem;
  }

  .header-title {
    font-size: 1.5rem;
  }

  .eventpay-content {
    padding: 1rem;
  }

  .eventpay-footer {
    padding: 1rem;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }

  .back-button,
  .prev-button,
  .next-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
